import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import { saveAs } from "file-saver";

interface LossesReportState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  filter: {
    dZoneGroups: any;
    targetDates: Date[];
  };

  documents: any[];
}

const state: LossesReportState = {
  loading: false,
  loaded: false,
  fatalError: false,

  filter: {
    dZoneGroups: null,
    targetDates: [new Date(), new Date()]
  },

  documents: []
};

function buildFilter(params: any) {
  const queryParams: any = {};

  if (params.targetDates) {
    queryParams.date_from = moment(params.targetDates[0]).format("YYYY-MM-DD");
    queryParams.date_to = moment(params.targetDates[1])
      .add(1, "day")
      .format("YYYY-MM-DD");
  }

  if (params.dZoneGroups) {
    queryParams.delivery_zone_id = params.dZoneGroups;
  }
  if (params.employeeId) {
    queryParams.employee_id = params.employeeId;
  }

  return queryParams;
}

export const REPORT_DATA = "REPORT_DATA";

export const actions: ActionTree<LossesReportState, RootState> = {
  loadData({ commit, state, dispatch }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      if (Object.values(params).length > 0) {
        commit("START_LOADING");

        vm.$db([
          vm
            .$q(REPORT_DATA)
            .raw("documents.list")
            .action({
              columns: {
                id: "s.id",
                shift_date: "s.shift_date",
                times_of_day: "s.times_of_day",
                mix_id: "vsm.id",
                plan_name: "vsm.plan_name",
                workers: "sw.workers",
                number: "sa.number",
                product_name: "p.name",
                fact_weight: "sfa.fact_weight",
                clots_weight: "sja.clots_weight",
                total_weight: { $sum: "sjai.value" }
              },
              from: "s:shifts",
              from_as: "s",
              joins: [
                {
                  join: {
                    columns: {
                      shift_id: "sw.shift_id",
                      workers: {
                        $array_agg: {
                          $obj: {
                            name: "e.name",
                            is_manager: "vr.is_manager",
                            loss_rate: "e.loss_rate"
                          }
                        }
                      }
                    },
                    from: "s:shift_workers",
                    from_as: "sw",
                    joins: [
                      {
                        join: "s:voc_roles",
                        type: "left",
                        as: "vr",
                        on: {
                          $and: [{ $eq: ["sw.role_id", "vr.id"] }]
                        }
                      },
                      {
                        join: "s:employees",
                        type: "left",
                        as: "e",
                        on: {
                          $eq: ["sw.employee_id", "e.id"]
                        }
                      }
                    ],
                    groups: ["sw.shift_id"]
                  },
                  type: "left",
                  as: "sw",
                  on: {
                    $eq: ["sw.shift_id", "s.id"]
                  }
                },
                {
                  join: "s:shift_atom",
                  type: "left",
                  as: "sa",
                  on: {
                    $eq: ["sa.shift_id", "s.id"]
                  }
                },
                {
                  join: "s:products",
                  type: "left",
                  as: "p",
                  on: {
                    $eq: ["sa.product_id", "p.id"]
                  }
                },
                {
                  join: "s:shift_fact_atom",
                  type: "left",
                  as: "sfa",
                  on: {
                    $eq: ["sfa.plan_id", "sa.id"]
                  }
                },
                {
                  join: "s:shift_journal_atom",
                  type: "left",
                  as: "sja",
                  on: {
                    $eq: ["sja.plan_id", "sa.coock_plan_id"]
                  }
                },
                {
                  join: "s:shift_journal_atom_ingredients",
                  type: "left",
                  as: "sjai",
                  on: {
                    $eq: ["sjai.journal_id", "sja.id"]
                  }
                },
                {
                  join: "s:shift_atom_coock_plan",
                  type: "left",
                  as: "sacp",
                  on: {
                    $eq: ["sacp.id", "sa.coock_plan_id"]
                  }
                },
                {
                  join: "s:shift_atom_coock_plan_items",
                  type: "left",
                  as: "sacpi",
                  on: {
                    $eq: ["sacp.id", "sacpi.coock_plan_id"]
                  }
                },
                {
                  join: "s:shift_plan_vdp",
                  type: "left",
                  as: "spv",
                  on: {
                    $eq: ["sacpi.vdp_plan_id", "spv.id"]
                  }
                },
                {
                  join: "s:view_mixes_rels",
                  type: "left",
                  as: "vmr",
                  on: {
                    $eq: ["spv.id", "vmr.id"]
                  }
                },
                {
                  join: "s:view_shift_mixes",
                  type: "left",
                  as: "vsm",
                  on: {
                    $eq: ["vsm.id", "vmr.base_id"]
                  }
                }
              ],
              page_size: 10000,
              where: {
                $and: [
                  { $gte: ["s.shift_date", { $: params.targetDates[0] }] },
                  { $lte: ["s.shift_date", { $: params.targetDates[1] }] },
                  { $lte: ["s.templates", { $: "atom" }] }
                ]
              },
              order: [{ $asc: ["s.shift_date"] }, { $asc: ["sa.number"] }],
              groups: [
                "s.id",
                "s.shift_date",
                "sw.workers",
                "sa.number",
                "p.name",
                "sfa.fact_weight",
                "sja.clots_weight",
                "vsm.id",
                "vsm.plan_name"
              ]
            })
        ]).then((result: ServerResult) => {
          const content = result.getOperation(REPORT_DATA).getContent();
          commit(
            "SET_REPORT_DATA",
            content.documents.map((item: any) => {
              item.loss_rate = 0.016;
              item.is_over = item.fact_weight < item.clots_weight;
              const managers =
                item.workers && item.workers.length > 0
                  ? item.workers.filter((item: any) => {
                      return item.is_manager;
                    })
                  : [];
              item.manager_name = managers.length ? managers[0].name : "";
              const operators =
                item.workers && item.workers.length > 0
                  ? item.workers.filter((item: any) => {
                      return !item.is_manager;
                    })
                  : [];
              item.operator_name = operators.length ? operators[0].name : "";
              item.loss_rate = operators.length
                ? operators[0].loss_rate / 100
                : 0.016;
              item.clots_weight =
                (parseFloat(item.total_weight) +
                  parseFloat(item.clots_weight) *
                    (parseFloat(item.clots_weight) < 1000 ? 1000 : 1)) /
                1000;
              // item.loss_rate = 0.016;
              return item;
            })
          );
          commit("LOADED_SUCCESS");
          resolve();
        });
      } else {
        resolve();
      }
    });
  },

  exportData({ state, getters }, { columns, columnsList }) {
    return new Promise((resolve, reject) => {
      const csvContent: any[] = [];
      const header: any[] = columnsList
        .filter((item: any) => {
          return columns.includes(item.id);
        })
        .map((item: any) => {
          return item.label;
        });
      const subheader: any[] = [
        "",
        "",
        "",
        "",
        "",
        "",
        Math.round(getters.total.clots_weight * 100) / 100,
        Math.round(getters.total.fact_weight * 100) / 100,
        Math.round(getters.total.deviant_middle * 100) / 100,
        Math.round(getters.total.deviant_middle * 100) / 100,
        "",
        Math.round(getters.total.deviant_precent * 100) / 100,
        Math.round(getters.total.deviant_precent * 100) / 100
      ];
      const colors: any[] = [];

      state.documents.map((item: any, row_idx: number) => {
        const row: any[] = [];
        columns.map((key: any, idx: number) => {
          let value: string = "";
          if (!colors[row_idx]) {
            colors[row_idx] = {};
          }
          if (idx > 5) {
            item[key] = item[key]
              ? Math.round(parseFloat(item[key]) * 100) / 100
              : 0;
          }
          switch (key) {
            case "shift_date":
              value =
                item.items && item.items.length > 0
                  ? moment(item.items[0][key]).format("DD.MM.YYYY")
                  : "";
              break;
            case "loss_rate":
              value = item[key]
                ? `${Math.round(item[key] * 100 * 100) / 100}`
                : "";
              break;
            case "deviant_over_precent":
              if (item.is_over) {
                colors[row_idx][key] = item.class_name;
                value = item[key]
                  ? `${Math.round(item[key] * 100 * 100) / 100}`
                  : "";
              }
              break;
            case "deviant_sur_precent":
              if (!item.is_over) {
                colors[row_idx][key] = item.class_name;
                value = item[key]
                  ? `${Math.round(item[key] * 100 * 100) / 100}`
                  : "";
              }
              break;
            default:
              value = item[key] ? item[key] : "";
          }
          row.push(value);
        });
        csvContent.push(row);
      });
      resolve({ csvContent: [header, subheader, ...csvContent], colors });
    });
  }
};

export const mutations: MutationTree<LossesReportState> = {
  START_LOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: state => {
    state.loaded = true;
    state.fatalError = true;
  },

  SET_REPORT_DATA(state, data) {
    state.documents = Object.values(
      data.reduce((acc: any, item: any, idx: number) => {
        const mix_id = item.mix_id ? item.mix_id : `${item.plan_name}-${idx}`;
        if (!acc[mix_id]) {
          acc[mix_id] = {
            plan_name: item.plan_name,
            manager_name: item.manager_name,
            operator_name: item.operator_name,
            product_name: item.product_name,
            number: parseInt(item.number),
            loss_rate: item.loss_rate,
            times_of_day: item.times_of_day,
            clots_weight: 0,
            fact_weight: 0,
            deviant_over: 0,
            deviant_sur: 0,
            loss_rate_orig: 0,
            is_over: false,
            items: []
          };
        }
        acc[mix_id].items.push(item);
        acc[mix_id].clots_weight += parseFloat(item.clots_weight) || 0;
        acc[mix_id].fact_weight += parseFloat(item.fact_weight) || 0;
        acc[mix_id].deviant_over += parseFloat(item.deviant_over) || 0;
        acc[mix_id].deviant_sur += parseFloat(item.deviant_sur) || 0;
        acc[mix_id].is_over =
          acc[mix_id].fact_weight < acc[mix_id].clots_weight;
        acc[mix_id].class_name = "clot-ok";
        if (acc[mix_id].is_over) {
          acc[mix_id].deviant_over =
            acc[mix_id].clots_weight - acc[mix_id].fact_weight;
          acc[mix_id].deviant_over_precent =
            acc[mix_id].clots_weight > 0
              ? Math.round(
                  (acc[mix_id].deviant_over / acc[mix_id].clots_weight) * 100
                ) / 100
              : 0;
          if (acc[mix_id].deviant_over_precent >= 0.016) {
            acc[mix_id].class_name = "clot-warn";
          }
        } else {
          acc[mix_id].deviant_sur =
            acc[mix_id].fact_weight - acc[mix_id].clots_weight;
          acc[mix_id].deviant_sur_precent =
            acc[mix_id].clots_weight > 0
              ? Math.round(
                  (acc[mix_id].deviant_sur / acc[mix_id].clots_weight) * 100
                ) / 100
              : 0;
          if (acc[mix_id].deviant_sur_precent <= 0.01) {
            acc[mix_id].class_name = "clot-middle";
          } else if (acc[mix_id].deviant_sur_precent > 0.01) {
            acc[mix_id].class_name = "clot-warn";
          }
        }
        return acc;
      }, {})
    );
  }
};

const getters = {
  total: (state: LossesReportState) => {
    return state.documents.reduce(
      (acc: any, item: any) => {
        acc.clots_weight += item.clots_weight || 0;
        acc.fact_weight += item.fact_weight;
        acc.deviant += item.deviant_over || 0;
        acc.deviant += item.deviant_sur || 0;
        acc.deviant_middle = acc.deviant / state.documents.length;
        acc.deviant_precent = (acc.deviant / acc.clots_weight) * 100;
        return acc;
      },
      {
        clots_weight: 0,
        fact_weight: 0,
        deviant: 0
      }
    );
  },
  exportData(state: LossesReportState) {
    const csvContent: any[] = [];
    const header = [
      "№ партии после мойки",
      "Дата и № партии",
      "База",
      "Закваска",
      "Рекомендуемая t заквашивания",
      "Продолжительность сквашивания, ч",
      "pH",
      "t, C"
    ];

    state.documents.map((item: any, idx: number) => {
      const row: any[] = [];
      row.push(idx + 1);
      row.push(
        moment(item.shift_date).format("DD.MM.YYYY") + " " + item.number
      );
      row.push(item.base_name);
      row.push(item.starter_type_name);
      row.push(item.starter_type_temp);
      row.push(item.fermentation_time);
      row.push(item.acidity_range.join(" - "));
      row.push(item.temp_range.join(" - "));
      //     })
      csvContent.push(row);
    });
    return [header, ...csvContent];
  }
};

export const STORE_KEY = "$_losses_report";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
